import type { STFolderMessage } from '../../st-folder-module'
import { compare } from '@st/json-patch'
import {
  createEditor,
  updateEditor,
  valueOf,
  type EditorState,
  type Edit
} from '~/st-doc-viewer/schema-editor/model'
import { pageGroupKey } from '~/st-doc-viewer/page-groups/page-group-key'
import type {
  PageGroup,
  PageGroupType,
  Jurisdiction,
  SetDocumentPageGroups,
  Contract,
  CreateTaxReturnDeliveryDraft,
  Signer,
  STDocument,
  UpdateContractDraft,
  UpdateDocumentData
} from '@st/sdk'
import { Effect } from 'effect'
import { SdkClient } from '~/effects'
import { scopeTask } from '@st/redux/src/task'
import {
  initContractEditor,
  reconcileContractEditor,
  contractEditorSigners,
  contractEditorIdle,
  contractEditorDirty,
  contractEditorTasks,
  updateContractEditor,
  contractEditorDefinition,
  signingComplete,
  type ContractEditorState,
  type ContractEditorMessage
} from '~/features/contract-editing/editor'
import type { AnnotationsPluginMessage } from '@st/document-viewer-kit/plugins/annotations'
import { selSelectedDoc } from '../../st-folder-selectors'
import { editablePageGroups } from '../../update-page-groups'
import { optimisticRequestsByType } from '../../operation-request-selectors'
import { clientFolderShareableLink } from '~/features/routes'
import {
  reducePageGroupEditor,
  materializePageGroupEditor,
  selectPageGroupEditor,
  type PageGroupEditorAction
} from './page-group-editor'
import { defSelector, Task, type TaskDeclaration } from '@st/redux'
import { match, P } from '@st/pattern'
import type {
  NavigationRequest,
  STFolderModuleState,
  STFolderReadyState
} from '../../st-folder-types'
import { contractForDocument } from '../document-contract'
import { deepEqual } from '@st/util/deep-equal'

export type TaxReturnDeliveryTab = 'summary' | 'signing' | 'bookmarks'

type PageGroupEdit = { status: 'closed' } | { status: 'editing'; group: PageGroup; key: string }

// Only the active editor exists. Each child owns its draft and interactions.
export type DeliveryEditor =
  | { type: 'summary'; draft: EditorState | null; catalog: Jurisdiction[] | null }
  | { type: 'signing'; draft: ContractEditorState }
  | { type: 'bookmarks'; edit: PageGroupEdit }
  | { type: 'creating' }

type Departure =
  | { type: 'tab'; tab: TaxReturnDeliveryTab }
  | { type: 'document'; request: NavigationRequest }

export type TaxReturnDeliveryState =
  | { phase: 'active'; editor: DeliveryEditor }
  | { phase: 'confirmingLeave'; editor: DeliveryEditor; destination: Departure }

export type TaxReturnDeliveryDocumentState = {
  contract: Contract
  hasOtherPublishedDelivery: boolean
}

/** Resolves this packet’s contract while retaining the folder-wide published-delivery constraint. */
export function taxReturnDeliveryDocumentState(
  publishedContract: Contract | undefined,
  drafts: Contract[],
  documentId: string
): TaxReturnDeliveryDocumentState | undefined {
  const contract =
    contractForDocument(publishedContract, documentId) ??
    drafts.find((draft) => contractForDocument(draft, documentId))

  if (!contract) return undefined

  return {
    contract,
    hasOtherPublishedDelivery: publishedContract != undefined && publishedContract.id != contract.id
  }
}

export type PageGroupMessage =
  | { type: 'pageGroupOpened'; group: PageGroup }
  | { type: 'pageGroupChanged'; action: PageGroupEditorAction }
  | { type: 'pageGroupCancelled' }
  | { type: 'pageGroupSaveRequested' }

export type TaxReturnDeliveryMessage =
  | { type: 'pageGroup'; message: PageGroupMessage }
  | { type: 'tabSelected'; tab: TaxReturnDeliveryTab }
  | { type: 'summaryEdited'; edit: Edit }
  | { type: 'draftCreateRequested' }
  | { type: 'contract'; message: ContractEditorMessage }
  | { type: 'summaryCatalogLoaded'; catalog: Jurisdiction[] }
  | { type: 'saveRequested' }
  | { type: 'leaveKept' }
  | { type: 'leaveDiscarded' }

/** Starts a fresh editing session; reconciliation selects Signing for drafts without analysis. */
export function initTaxReturnDeliveryState(): TaxReturnDeliveryState {
  return { phase: 'active', editor: { type: 'summary', draft: null, catalog: null } }
}

/** Summary demand belongs to the active phase, including its retained departure dialog. */
const loadSummaryCatalog = Task.define((_folderId: string, _documentId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const catalog = yield* sdk.send({ type: 'schema/jurisdictions' }).pipe(Effect.orDie)
    return {
      type: 'taxReturnDelivery',
      message: { type: 'summaryCatalogLoaded', catalog }
    } as const
  })
)

export function taxReturnDeliveryTasks(
  folder: STFolderModuleState
): TaskDeclaration<STFolderMessage>[] {
  if (folder.status != 'ready') return []
  const { contract } = deliveryContext(folder)
  if (contract?.status != 'draft') return []
  return match(folder.taxReturnDelivery.editor)
    .with({ type: 'summary', catalog: null }, () => [
      loadSummaryCatalog(folder.folderId, folder.selectedDocId!)
    ])
    .with({ type: 'signing' }, ({ draft }) =>
      match(folder.taxReturnDelivery.phase)
        .with('active', () => contractEditorTasks(draft))
        .with('confirmingLeave', () => [])
        .exhaustive()
        .map((task) =>
          scopeTask(
            ['taxReturnDelivery', 'contract'],
            task,
            (message): STFolderMessage => ({
              type: 'taxReturnDelivery',
              message: { type: 'contract', message }
            })
          )
        )
    )
    .otherwise(() => [])
}

/** Departure keeps the current editor intact; only confirmation discards it. */
export function requestDeliveryDeparture(
  state: TaxReturnDeliveryState,
  destination: Departure
): TaxReturnDeliveryState {
  return { phase: 'confirmingLeave', editor: state.editor, destination }
}

function editorForTab(contract: Contract | undefined, tab: TaxReturnDeliveryTab): DeliveryEditor {
  return match(tab)
    .with('summary', () => ({ type: 'summary' as const, draft: null, catalog: null }))
    .with('bookmarks', () => ({ type: 'bookmarks' as const, edit: { status: 'closed' as const } }))
    .with('signing', () => ({ type: 'signing' as const, draft: initContractEditor(contract!.id!) }))
    .exhaustive()
}

/**
 * Whether processing extracted this packet's return values, which the Summary
 * and the delivery checklist read. A manual draft has no analysis, and a 1065
 * packet records only its signing boxes, so neither has `tax_year`.
 */
export function hasReturnAnalysis(document: Pick<STDocument, 'data'>): boolean {
  return document.data.tax_year != undefined
}

// Only individual returns with a lone taxpayer can gain a spouse; business signers keep their role.
function canAddSpouse(signers: Signer[]): boolean {
  return signers.length == 1 && signers[0].role == 'taxpayer'
}

const selSummaryBase = (state: STFolderModuleState) => {
  if (state.status != 'ready') return undefined
  return state.folderState.documents.find((doc) => doc.id == state.selectedDocId)?.data
}
const selSummaryEditorState = (state: STFolderModuleState) => {
  if (state.status != 'ready') return null
  return match(state.taxReturnDelivery.editor)
    .with({ type: 'summary' }, ({ draft }) => draft)
    .otherwise(() => null)
}
// One derivation for the view and the reducer, so both address the same node
// identities before the first edit is stored.
export const selTaxReturnDeliveryEditor = defSelector(
  [selSummaryBase, selSummaryEditorState],
  (data, editor) => editor ?? createEditor(data)
)

// A stored editor is dirty when a creation is pending or its value moved away
// from the server value it was reconciled against.
function summaryEdited(editor: EditorState | null): boolean {
  return (
    editor != null &&
    (editor.creation != null || !deepEqual(valueOf(editor.root), valueOf(editor.base)))
  )
}

/** Resolve the selected packet once; this is derived context, never another stored draft. */
function deliveryContext(folder: STFolderReadyState) {
  const document = selSelectedDoc(folder)
  const delivery =
    document &&
    taxReturnDeliveryDocumentState(
      folder.folderState.taxReturnDelivery,
      folder.folderState.taxReturnDeliveryDrafts,
      document.id
    )
  const contract = delivery?.contract
  const editor = folder.taxReturnDelivery.editor
  const signers = match(editor)
    .with({ type: 'signing' }, ({ draft }) => contractEditorSigners(draft, contract?.signers ?? []))
    .otherwise(() => contract?.signers ?? [])
  return {
    document,
    contract,
    hasOtherPublishedDelivery: delivery?.hasOtherPublishedDelivery == true,
    editor,
    signers,
    viewer: folder.viewer,
    viewerScope: folder.viewerScope,
    requests: folder.requestQueue.items,
    archived: folder.folderState.folder.archived == true,
    active: folder.taxReturnDelivery.phase == 'active'
  }
}

type DeliveryContext = ReturnType<typeof deliveryContext>

/** Bookmark rows save independently; summary and signing share departure protection. */
function deliveryDirty({ contract, editor, viewer, viewerScope }: DeliveryContext): boolean {
  if (viewerScope != 'pdf' || contract?.status != 'draft') return false
  return match(editor)
    .with({ type: P.union('bookmarks', 'creating') }, () => false)
    .with({ type: 'summary' }, ({ draft }) => summaryEdited(draft))
    .with({ type: 'signing' }, ({ draft }) => contractEditorDirty(draft, contract, viewer))
    .exhaustive()
}

export function selTaxReturnDeliveryDirty(folder: STFolderModuleState): boolean {
  return folder.status == 'ready' && deliveryDirty(deliveryContext(folder))
}

/** Queued and running writes both reserve this packet against a repeated save. */
function deliverySaving({ contract, document, requests }: DeliveryContext): boolean {
  return requests.some((item) => {
    if (item.status != 'queued' && item.status != 'running') return false
    return match(item.op.request)
      .with(
        {
          type: P.union(
            'folders/updateDocumentData',
            'folders/regenerateTaxReturnSummary',
            'folders/createTaxReturnDeliveryDraft'
          )
        },
        (request) => request.documentId == document?.id
      )
      .with(
        { type: 'folders/updateContractDraft' },
        (request) => request.contractId == contract?.id
      )
      .otherwise(() => false)
  })
}

export function selTaxReturnDeliverySaving(folder: STFolderModuleState): boolean {
  return folder.status == 'ready' && deliverySaving(deliveryContext(folder))
}

/** Product policy consumes one resolved context; views never reconstruct editing phases. */
function deliveryPolicy(context: DeliveryContext) {
  const { document, contract, hasOtherPublishedDelivery, editor, signers, archived, active } =
    context
  const saveState = match({
    saving: deliverySaving(context),
    dirty: deliveryDirty(context)
  })
    .with({ saving: true }, () => 'saving' as const)
    .with({ dirty: true }, () => 'dirty' as const)
    .otherwise(() => 'saved' as const)
  const canEdit = contract?.status == 'draft' && !archived && saveState != 'saving' && active
  const idle = match(editor)
    .with({ type: 'signing' }, ({ draft }) => contractEditorIdle(draft))
    .otherwise(() => true)
  const canEditFields = canEdit && editor.type == 'signing' && idle
  const signingReady = contract != undefined && signingComplete(contract)
  return {
    saveState,
    canEdit,
    hasSummary: document != undefined && hasReturnAnalysis(document),
    canEditFields,
    canCreateDraft:
      contract == undefined &&
      document?.documentTypeId == 'tax_return_packet' &&
      document.processingStatus == 'processed' &&
      !archived &&
      saveState != 'saving',
    addableRoles: match(canEditFields && canAddSpouse(signers))
      .with(true, () => ['spouse'])
      .otherwise(() => []),
    removableSignerIds: signers
      .filter((signer) => signer.role == 'spouse')
      .map((signer) => signer.id),
    canSave:
      canEdit &&
      saveState == 'dirty' &&
      idle &&
      !(editor.type == 'summary' && editor.draft?.creation != null) &&
      signers.every((signer) => (signer.displayName ?? '').trim() != ''),
    signingReady,
    canDeliver:
      active &&
      idle &&
      contract != undefined &&
      contract.status != 'submitted' &&
      !hasOtherPublishedDelivery &&
      signingReady &&
      !archived &&
      saveState == 'saved'
  }
}

/** Rendered delivery controls belong to a ready folder; lifecycle guards use dirty/saving instead. */
export function selTaxReturnDeliveryPolicy(folder: STFolderModuleState) {
  if (folder.status != 'ready') throw new Error('Delivery controls require a ready folder')
  return deliveryPolicy(deliveryContext(folder))
}

/** Refresh only the active child. Unsaved values survive until the server acknowledges them. */
export function reconcileTaxReturnDeliveryState(folder: STFolderReadyState): STFolderReadyState {
  const { document, contract } = deliveryContext(folder)
  const current = folder.taxReturnDelivery.editor
  const editor = match(current)
    .returnType<DeliveryEditor>()
    .with({ type: 'creating' }, () => {
      if (contract?.status != 'draft') return current
      return editorForTab(contract, 'signing')
    })
    .with({ type: 'bookmarks' }, (state) => {
      const edit = state.edit
      if (
        edit.status == 'editing' &&
        (!document ||
          !editablePageGroups(document).some((group) => pageGroupKey(group) == edit.key))
      )
        return { type: 'bookmarks', edit: { status: 'closed' } }
      return state
    })
    .with({ type: 'signing' }, (state) => {
      if (contract?.status != 'draft') return state
      const draft = reconcileContractEditor(state.draft, contract)
      if (draft == state.draft) return state
      return { ...state, draft }
    })
    .with({ type: 'summary' }, (state) => {
      if (document && contract?.status == 'draft' && !hasReturnAnalysis(document))
        return editorForTab(contract, 'signing')
      const draft = state.draft
      if (!document || !draft) return state
      if (deepEqual(document.data, valueOf(draft.root))) {
        if (draft.base == draft.root) return state
        return { ...state, draft: { ...draft, base: draft.root } }
      }
      if (summaryEdited(draft)) return state
      return { ...state, draft: null }
    })
    .exhaustive()
  if (editor == current) return folder
  return { ...folder, taxReturnDelivery: { ...folder.taxReturnDelivery, editor } }
}

type TaxReturnDeliveryUpdate = { state: TaxReturnDeliveryState } & (
  | { type: 'stay' }
  | { type: 'leave'; navigation: NavigationRequest | null }
  | { type: 'viewer'; messages: AnnotationsPluginMessage[] }
  | {
      type: 'save'
      request:
        | UpdateContractDraft
        | CreateTaxReturnDeliveryDraft
        | UpdateDocumentData
        | SetDocumentPageGroups
      onSuccess?: () => STFolderMessage
      failureMessage: string
    }
)

/** One transition table exposes departure, editor, and nested bookmark phases. */
export function updateTaxReturnDelivery(
  folder: STFolderReadyState,
  message: TaxReturnDeliveryMessage
): TaxReturnDeliveryUpdate {
  const state = folder.taxReturnDelivery
  const context = deliveryContext(folder)
  const policy = deliveryPolicy(context)
  const { document, contract } = context
  // Child edits preserve the parent's phase and departure destination.
  const stay = (editor: DeliveryEditor): TaxReturnDeliveryUpdate => ({
    type: 'stay',
    state: { ...state, editor }
  })
  return (
    match({ state, editor: state.editor, message })
      .returnType<TaxReturnDeliveryUpdate>()
      .with(
        { editor: { type: 'summary' }, message: { type: 'summaryCatalogLoaded' } },
        ({ editor, message }) => stay({ ...editor, catalog: message.catalog })
      )
      .with({ state: { phase: 'confirmingLeave' }, message: { type: 'leaveKept' } }, () => ({
        type: 'stay',
        state: { phase: 'active', editor: state.editor }
      }))
      .with(
        {
          state: { phase: 'confirmingLeave', destination: { type: 'tab' } },
          message: { type: 'leaveDiscarded' }
        },
        ({ state }) => ({
          type: 'leave',
          state: { phase: 'active', editor: editorForTab(contract, state.destination.tab) },
          navigation: null
        })
      )
      .with(
        {
          state: { phase: 'confirmingLeave', destination: { type: 'document' } },
          message: { type: 'leaveDiscarded' }
        },
        ({ state }) => ({
          type: 'leave',
          state: initTaxReturnDeliveryState(),
          navigation: state.destination.request
        })
      )
      // Catalog completion and departure decisions remain valid while a save settles.
      .with(
        P._,
        () => state.phase != 'active' || policy.saveState == 'saving',
        () => {
          throw new Error(
            `Unexpected delivery ${message.type} during ${state.phase}/${policy.saveState}`
          )
        }
      )
      .with({ message: { type: 'tabSelected' } }, ({ message: { tab } }) => {
        if (tab == state.editor.type) return { type: 'stay', state }
        if (policy.saveState == 'dirty')
          return { type: 'stay', state: requestDeliveryDeparture(state, { type: 'tab', tab }) }
        return {
          type: 'leave',
          state: { phase: 'active', editor: editorForTab(contract, tab) },
          navigation: null
        }
      })
      .with({ message: { type: 'draftCreateRequested' } }, () => {
        if (!policy.canCreateDraft)
          throw new Error('Cannot create a delivery draft for this document')
        return {
          type: 'save',
          state: { phase: 'active', editor: { type: 'creating' } },
          request: {
            type: 'folders/createTaxReturnDeliveryDraft',
            folderId: folder.folderId,
            documentId: document!.id
          },
          failureMessage: 'The delivery draft could not be created. Please try again.'
        }
      })
      .otherwise(() => {
        // Child operations receive a real draft and document, never folder lookup rules.
        if (!policy.canEdit || !document || !contract)
          throw new Error('Delivery editing requires an editable draft and its source document')
        if (message.type == 'saveRequested' && !policy.canSave)
          throw new Error('Cannot save a non-editable or clean delivery')
        const unexpected = (): never => {
          throw new Error(`Unexpected ${message.type} in ${state.phase}/${state.editor.type}`)
        }
        return match(state.editor)
          .returnType<TaxReturnDeliveryUpdate>()
          .with({ type: 'summary' }, (editor) =>
            match(message)
              .returnType<TaxReturnDeliveryUpdate>()
              .with({ type: 'summaryEdited' }, ({ edit }) =>
                stay({ ...editor, draft: updateEditor(selTaxReturnDeliveryEditor(folder), edit) })
              )
              .with({ type: 'saveRequested' }, () => ({
                type: 'save',
                state,
                request: {
                  type: 'folders/updateDocumentData',
                  folderId: folder.folderId,
                  documentId: document.id,
                  diff: compare(document.data, valueOf(editor.draft!.root) as object)
                },
                onSuccess: () => ({
                  type: 'requestEnqueued',
                  request: {
                    type: 'folders/regenerateTaxReturnSummary',
                    folderId: folder.folderId,
                    documentId: document.id
                  }
                }),
                failureMessage: 'The corrected summary could not be saved. Please check the values.'
              }))
              .otherwise(unexpected)
          )
          .with({ type: 'signing' }, ({ draft }) =>
            match(message)
              .returnType<TaxReturnDeliveryUpdate>()
              .with({ type: 'contract' }, ({ message }) => {
                const update = updateContractEditor(
                  draft,
                  contract,
                  context.viewer,
                  message,
                  policy
                )
                return {
                  type: 'viewer',
                  state: { ...state, editor: { type: 'signing', draft: update.state } },
                  messages: update.viewerMessages
                }
              })
              .with({ type: 'saveRequested' }, () => ({
                type: 'save',
                state,
                request: {
                  type: 'folders/updateContractDraft',
                  folderId: folder.folderId,
                  contractId: contract.id!,
                  ...contractEditorDefinition(draft, contract, context.viewer)
                },
                failureMessage: 'Signers and fields could not be saved. Please try again.'
              }))
              .otherwise(unexpected)
          )
          .with({ type: 'bookmarks' }, ({ edit }) =>
            match(message)
              .with({ type: 'pageGroup' }, ({ message }) => {
                const types = pageGroupTypes(folder, document)
                return match({ edit, message })
                  .returnType<TaxReturnDeliveryUpdate>()
                  .with(
                    { edit: { status: 'editing' }, message: { type: 'pageGroupSaveRequested' } },
                    ({ edit }) => {
                      if (!selectPageGroupEditor(edit.group, types).canSave)
                        throw new Error('Cannot save an incomplete page-group draft')
                      return {
                        type: 'save',
                        state: {
                          ...state,
                          editor: { type: 'bookmarks', edit: { status: 'closed' } }
                        },
                        request: {
                          type: 'folders/setDocumentPageGroups',
                          folderId: folder.folderId,
                          documentId: document.id,
                          pageGroups: editablePageGroups(document).map((group) =>
                            match(pageGroupKey(group) == edit.key)
                              .with(true, () => materializePageGroupEditor(edit.group, types))
                              .otherwise(() => group)
                          )
                        },
                        failureMessage: 'Page groups could not be saved. Please try again.'
                      }
                    }
                  )
                  .otherwise(() =>
                    stay({ type: 'bookmarks', edit: updateBookmarkEdit(edit, message, types) })
                  )
              })
              .otherwise(unexpected)
          )
          .with({ type: 'creating' }, unexpected)
          .exhaustive()
      })
  )
}

/** Bookmark editing is local; only its parent can persist a completed row. */
function updateBookmarkEdit(
  edit: PageGroupEdit,
  message: PageGroupMessage,
  types: PageGroupType[]
): PageGroupEdit {
  return match({ edit, message })
    .returnType<PageGroupEdit>()
    .with({ message: { type: 'pageGroupOpened' } }, ({ message: { group } }) => ({
      status: 'editing',
      group,
      key: pageGroupKey(group)
    }))
    .with({ edit: { status: 'editing' }, message: { type: 'pageGroupCancelled' } }, () => ({
      status: 'closed'
    }))
    .with(
      { edit: { status: 'editing' }, message: { type: 'pageGroupChanged' } },
      ({ edit, message }) => ({
        ...edit,
        group: reducePageGroupEditor(edit.group, message.action, types)
      })
    )
    .otherwise(() => {
      throw new Error('Page-group action requires an open draft')
    })
}

function pageGroupTypes(folder: STFolderReadyState, document: STDocument) {
  return folder.folderState.documentTypes.find((type) => type.id == document.documentTypeId)!
    .pageGroupTypes
}

/** Local signer edits override the selected packet's persisted definition. */
export function selSigningSigners(folder: STFolderModuleState): Signer[] {
  if (folder.status != 'ready') return []
  return deliveryContext(folder).signers
}

/**
 * The Karbon delivery dialog. A publish request stays `publishing` until the
 * folder update shows the published delivery, so success never flashes back to
 * unpublished.
 */
export function selKarbonDeliveryDialog(state: STFolderModuleState) {
  if (state.status != 'ready' || !state.karbonDeliveryDialog) return undefined
  const { contractId } = state.karbonDeliveryDialog
  return {
    url: clientFolderShareableLink({
      organizationSlug: state.folderState.organization.slug,
      folderId: state.folderId,
      aud: 'karbon'
    }),
    publish: { type: 'folders/publishContract', folderId: state.folderId, contractId } as const,
    publication: match({
      published: state.folderState.taxReturnDelivery?.id == contractId,
      publishing:
        optimisticRequestsByType(state.requestQueue.items, {
          type: 'folders/publishContract',
          filter: { contractId }
        }).length > 0
    })
      .returnType<'unpublished' | 'publishing' | 'published'>()
      .with({ published: true }, () => 'published')
      .with({ published: false, publishing: true }, () => 'publishing')
      .with({ published: false, publishing: false }, () => 'unpublished')
      .exhaustive()
  }
}
